import './AppBar.scss';

import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { getClient } from 'src/module/authentication/selector';
import ehtLogoColored from 'src/assets/EHTG_Logo_SmartTrace_FullColour_RGB.png';
import genericLogo from 'src/assets/generic-company-logo.svg';
import Tooltip from '@mui/material/Tooltip';
import SupportIcon from '@mui/icons-material/SupportSharp';
import { isWhitelabel } from 'src/utils/utils';
import { getClientAvatar } from 'src/service/api';
import { useTranslation } from 'src/i18n';

export default function AppBar () {
  const { t } = useTranslation();
  const client = useSelector(getClient);
  const [fetchedAvatarUrl, setFetchedAvatarUrl] = useState<string | null>(null);
  const isLoadingRef = useRef(false);

  const avatarUrl = client.get('avatar_url');
  const avatarId = client.get('avatar_id');
  const clientId = client.get('id');
  const clientName = client.get('name');
  const clientAvatarAlt = `${clientName} Avatar Url`;
  const whitelabel = isWhitelabel();

  // Fetch avatar from API if avatar_id exists and we don't have avatar_url
  useEffect(() => {
    const fetchAvatar = async () => {
      if (avatarId && !avatarUrl && clientId && !isLoadingRef.current) {
        isLoadingRef.current = true;
        try {
          // Create action object for the request function
          const action = { currentClientId: clientId, clientId: clientId };
          const blobUrl = await getClientAvatar(action, clientId);
          setFetchedAvatarUrl(blobUrl);
        } catch (error) {
          setFetchedAvatarUrl(null);
        } finally {
          isLoadingRef.current = false;
        }
      }
      else {
        setFetchedAvatarUrl(null);
      }
    };

    fetchAvatar();
  }, [avatarId, avatarUrl, clientId]);

  // Clean up blob URL when component unmounts or avatar changes
  useEffect(() => {
    return () => {
      if (fetchedAvatarUrl) {
        URL.revokeObjectURL(fetchedAvatarUrl);
      }
    };
  }, [fetchedAvatarUrl]);

  const img = () => {
    // Use avatar_url if available (legacy)
    if (avatarUrl) {
      return <img src={avatarUrl} alt={clientAvatarAlt} />;
    }

    // Use fetched avatar from API if available
    if (fetchedAvatarUrl) {
      return <img src={fetchedAvatarUrl} alt={clientAvatarAlt} />;
    }

    // Fallback to generic or EHT logo
    if (whitelabel) {
      return <img src={genericLogo} alt={clientAvatarAlt} />;
    }
    return <img src={ehtLogoColored} alt={clientAvatarAlt} />;
  };

  return (
    <div data-testid='app-bar' className='app-bar'>
      <div className='app-bar-container'>
        <div className='app-bar-main'>
          {img()}
          <span>{clientName}</span>
          <div className='app-bar-right'>
            {
              !whitelabel ? <a href='https://go.chemelex.com/knowledge' target='_blank' rel='noopener noreferrer' className='knowledge-base-link'>
                <Tooltip title={t('nav.knowledgeBase')}>
                  <SupportIcon className="knowledge-icon" />
                </Tooltip>
                <span className='knowledge-base-label'>{t('nav.knowledgeBase')}</span>
              </a> : null
            }
          </div>
        </div>
      </div>
    </div>
  );
}
